import type { LucideIcon } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export type MetricTone = "primary" | "secondary" | "accent" | "destructive" | "muted";

const TONE: Record<MetricTone, { disc: string; iconText: string; rail: string }> = {
  primary:     { disc: "bg-primary/15 ring-1 ring-primary/30",         iconText: "text-primary",          rail: "bg-primary" },
  secondary:   { disc: "bg-secondary/15 ring-1 ring-secondary/30",     iconText: "text-secondary",        rail: "bg-secondary" },
  accent:      { disc: "bg-accent/25 ring-1 ring-accent/50",           iconText: "text-foreground",       rail: "bg-accent" },
  destructive: { disc: "bg-destructive/15 ring-1 ring-destructive/30", iconText: "text-destructive",      rail: "bg-destructive" },
  muted:       { disc: "bg-muted ring-1 ring-border",                  iconText: "text-muted-foreground", rail: "bg-border" },
};

/**
 * Compact KPI card: left tone rail, label + tone-tinted icon disc, big value,
 * optional caption. Pass `onClick` to make it a button (e.g. click-to-filter);
 * `active` shows the selected ring.
 */
export function MetricCard({
  icon: Icon,
  tone,
  label,
  value,
  caption,
  onClick,
  active = false,
}: {
  icon: LucideIcon;
  tone: MetricTone;
  label: string;
  value: string | number;
  caption?: string;
  onClick?: () => void;
  active?: boolean;
}) {
  const t = TONE[tone];
  const interactive = typeof onClick === "function";
  return (
    <Card
      className={cn(
        "relative overflow-hidden transition-shadow",
        interactive &&
          "cursor-pointer hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        active && "ring-2 ring-foreground",
      )}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-pressed={interactive ? active : undefined}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
    >
      <div className={cn("absolute inset-y-0 left-0 w-1", t.rail)} />
      <CardContent className="p-4 pl-5">
        <div className="flex items-start justify-between gap-2">
          <div className="text-[10px] uppercase tracking-[0.1em] font-semibold text-muted-foreground">
            {label}
          </div>
          <div className={cn("size-7 rounded-full grid place-items-center", t.disc)}>
            <Icon size={14} strokeWidth={2} className={t.iconText} />
          </div>
        </div>
        <div className="mt-1 text-2xl font-bold tabular-nums">{value}</div>
        {caption ? <div className="mt-0.5 text-xs text-muted-foreground">{caption}</div> : null}
      </CardContent>
    </Card>
  );
}
